import { createSlice, Dispatch } from '@reduxjs/toolkit';
import { setEventData } from './events';
import { setShowLoader } from './loader';
import { setShowError } from './error';
import { sendAnalyticsEvents } from '../../analytics';
import { EVENTS } from '../constants';
import {
  type Stripe as StripeType,
  type StripeCardNumberElement,
  type ConfirmCardPaymentData,
} from '@stripe/stripe-js';
import {
  getPollingID,
  getSubscriptionID,
  sendStripeReq,
  subscribeToPriceChange,
} from './checkoutFetch';

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState: {
    cardholderName: '',
    isSuccess: null,
    stripeNonce: '',
    braintreeNonce: '',
    id: null,
    paymentError: {
      show: false,
      message: '',
    },
    isCardFormShown: false,
    showSuccessPopup: false,
    openPopoverId: null,
    showUpsellGuideSuccessPopup: false,
  },
  reducers: {
    setCardholderName: (state, action) => {
      state.cardholderName = action.payload;
    },
    setRequestResult: (state, action) => {
      const { isSuccess, id } = action.payload;

      state.isSuccess = isSuccess;
      state.id = id;
    },
    setShowPaymentError: (state, action) => {
      const show: boolean | string = action.payload;
      if (typeof show === 'string') {
        state.paymentError = {
          show: true,
          message: show,
        };
      } else {
        state.paymentError = {
          show: show,
          message: '',
        };
      }
    },
    setIfCardFormShown: (state, action) => {
      state.isCardFormShown = action.payload;
    },
    setStripeNonce: (state, action) => {
      state.stripeNonce = action.payload;
    },
    setBraintreeNonce: (state, action) => {
      state.braintreeNonce = action.payload;
    },
    setShowSuccessPopup: (state, action) => {
      state.showSuccessPopup = action.payload;
    },
    setOpenPopoverId: (state, action) => {
      state.openPopoverId = action.payload;
    },
    setShowUpsellGuideSuccessPopup: (state, action) => {
      state.showUpsellGuideSuccessPopup = action.payload;
    },
  },
});

export const {
  setCardholderName,
  setStripeNonce,
  setBraintreeNonce,
  setRequestResult,
  setShowPaymentError,
  setIfCardFormShown,
  setShowSuccessPopup,
  setOpenPopoverId,
  setShowUpsellGuideSuccessPopup,
} = checkoutSlice.actions;

interface StripePurchaseProps {
  paymentMethodObj: {
    methodName: string;
    stripeNonce: string;
  } | null;
  stripe: StripeType;
  card: StripeCardNumberElement | null;
  name?: string;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  successPayment?: (id1: string, id2: string, price: number) => void;
  failedPayment?: () => void;
  coupon?: string;
}

interface ServerRequestProps {
  paymentMethodObj: {
    methodName?: string;
    stripeNonce: string;
  } | null;
  stripe: StripeType | null;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  onSuccess?: any;
  onError?: any;
  payment_intent_id?: string;
  coupon?: string;
}

interface StripeUpsellProps {
  stripeNonce?: string;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  onSuccessEvents?: any;
  onErrorEvents?: any;
}

interface BraintreePurchaseProps {
  nonce: string;
  binData?: any;
  threeDSInstance?: any;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  coupon?: string;
  successPayment?: (id1: string, id2: string, price: number) => void;
}

interface BraintreeUpsellProps {
  braintreeNonce: string;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  onSuccessEvents?: any;
  onErrorEvents?: any;
}

const sendServerRequest = async ({
  stripe,
  paymentMethodObj,
  plan,
  token,
  analyticsParams,
  eventsData,
  onError,
  onSuccess,
  payment_intent_id = '',
  coupon = '',
}: ServerRequestProps) => {
  const { stripeNonce, methodName } = paymentMethodObj ?? {};
  const pollingID = await getPollingID(
    stripeNonce ?? '',
    plan,
    token,
    analyticsParams,
    eventsData,
    onError,
    payment_intent_id,
    coupon,
  );
  if (pollingID) {
    const purchaseRequest = await subscribeToPriceChange(pollingID);
    const { status, errors, data } = purchaseRequest || {
      errors: { messages: 'Unknown error. Please try again' },
    };

    if (status) {
      if (status === 'requires_action' && data && data.payment_intent_secret && stripe) {
        const cardPaymentSecret = data.payment_intent_secret;
        const cardPaymentData: ConfirmCardPaymentData = methodName
          ? { payment_method: stripeNonce }
          : { setup_future_usage: 'off_session' };
        const cardPaymentOptions = methodName ? { handleActions: false } : {};

        const { error: errorAction, paymentIntent } = await stripe.confirmCardPayment(
          cardPaymentSecret,
          cardPaymentData,
          cardPaymentOptions,
        );

        if (errorAction) {
          if (methodName) {
            onError(errorAction.message, data);
          } else {
            await sendServerRequest({
              stripe,
              paymentMethodObj,
              plan,
              token,
              analyticsParams,
              eventsData,
              onError,
              onSuccess,
              payment_intent_id: data.payment_intent_id,
              coupon,
            });
          }
          return;
        }
        if (methodName) {
          if (paymentIntent.status === 'requires_action') {
            const { error } = await stripe.confirmCardPayment(data.payment_intent_secret);
            if (error) {
              onError(error, data);
              return;
            }
          }
        }

        await sendServerRequest({
          stripe,
          paymentMethodObj,
          plan,
          token,
          analyticsParams,
          eventsData,
          onError,
          onSuccess,
          payment_intent_id: paymentIntent.id,
          coupon,
        });
      } else {
        const { subscription_id, event_id, price_with_discount, customer_id } = data || {
          subscription_id: '',
          event_id: '',
          price_with_discount: 0,
        };

        return onSuccess(subscription_id, event_id, {
          price_with_discount,
          ...(customer_id != null && customer_id !== '' ? { customer_id } : {}),
        });
      }
    }

    if (errors) {
      if (status === 'requires_action') return;
      onError(errors?.messages || 'An error has occurred', data);
    }
  }
};

export const stripePurchase =
  ({
    paymentMethodObj = null,
    stripe,
    card,
    name = '',
    plan,
    token,
    analyticsParams,
    eventsData,
    successPayment,
    failedPayment,
    coupon,
  }: StripePurchaseProps) =>
  async (dispatch: Dispatch) => {
    const onError = (data: any, errorData: any) => {
      failedPayment?.();

      let txt = data;
      if (typeof txt !== 'string') txt = data?.message;
      if (typeof txt !== 'string') txt = 'Payment error';

      dispatch(
        setEventData({
          declineCode: errorData?.decline_code || errorData?.processor_response,
        }),
      );
      sendAnalyticsEvents(EVENTS.PAYMENT_DECLINE, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      dispatch(setShowPaymentError(txt));
    };
    const onSuccess = (
      sub_id: string,
      event_id: string,
      paymentData?: { price_with_discount?: number; customer_id?: string | number },
    ) => {
      const price_with_discount = paymentData?.price_with_discount ?? 0;
      successPayment?.(sub_id, event_id, price_with_discount);
      dispatch(
        setRequestResult({
          isSuccess: true,
          id: sub_id,
        }),
      );
      const localData = {
        subscription_id: sub_id,
        event_id,
        upsell: analyticsParams?.is_upsell || false,
        plan_id: plan,
        ...(paymentData?.customer_id != null && paymentData.customer_id !== ''
          ? { customer_id: paymentData.customer_id }
          : {}),
      };
      dispatch(setEventData(localData));
      sendAnalyticsEvents(EVENTS.SUBSCRIBED, { ...eventsData, ...localData });
    };
    const sendReq = (obj: any) =>
      sendServerRequest({
        stripe,
        paymentMethodObj: obj,
        plan,
        token,
        analyticsParams,
        eventsData,
        onError,
        onSuccess,
        payment_intent_id: '',
        coupon,
      });

    dispatch(setShowLoader(true));

    try {
      if (paymentMethodObj) {
        dispatch(setStripeNonce(paymentMethodObj.stripeNonce));
        await sendReq(paymentMethodObj);
      } else if (card) {
        const { paymentMethod, error } = await sendStripeReq(stripe, card, name);
        const stripeNonce = paymentMethod?.id || error?.code;

        if (error) {
          console.error(error.message);
          dispatch(setShowError(error.message));
        } else {
          dispatch(setStripeNonce(stripeNonce));
          await sendReq({ stripeNonce });
        }
      } else {
        dispatch(setShowError('Payment form is not ready'));
      }
    } finally {
      dispatch(setShowLoader(false));
    }
  };

export const sendStripeUpsellRequest =
  ({
    stripeNonce,
    plan,
    token,
    analyticsParams,
    eventsData,
    onSuccessEvents,
    onErrorEvents,
  }: StripeUpsellProps) =>
  async (dispatch: any) => {
    function onError() {
      if (onErrorEvents) onErrorEvents(...arguments);
    }
    function onSuccess(
      sub_id: string,
      event_id: string,
      paymentData?: { price_with_discount?: number; customer_id?: string | number },
    ) {
      if (onSuccessEvents) {
        onSuccessEvents(
          sub_id,
          event_id,
          paymentData?.price_with_discount,
          paymentData?.customer_id != null && paymentData.customer_id !== ''
            ? { customer_id: paymentData.customer_id }
            : undefined,
        );
      }
    }

    dispatch(setShowLoader(true));

    try {
      await sendServerRequest({
        stripe: null,
        paymentMethodObj: stripeNonce ? { stripeNonce } : null,
        plan,
        token,
        analyticsParams,
        eventsData,
        onError,
        onSuccess,
        payment_intent_id: '',
        coupon: '',
      });
    } finally {
      dispatch(setShowLoader(false));
    }
  };

export const braintreePurchase =
  ({
    nonce,
    binData,
    threeDSInstance,
    plan,
    token,
    analyticsParams,
    eventsData,
    coupon,
    successPayment,
  }: BraintreePurchaseProps) =>
  async (dispatch: Dispatch) => {
    dispatch(setBraintreeNonce(nonce));

    const onError = (data: any, errorData?: any) => {
      let txt = data;
      if (typeof txt !== 'string') txt = data.message;
      if (typeof txt !== 'string') txt = 'Payment error';

      dispatch(
        setEventData({
          id: 'decline_code',
          data: errorData?.decline_code || errorData?.processor_response,
        }),
      );
      sendAnalyticsEvents(EVENTS.PAYMENT_DECLINE, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      dispatch(setShowPaymentError(txt));
      dispatch(setShowLoader(false));
    };
    const onSuccess = ({
      customer_id,
      subscription_id,
      event_id,
      price_with_discount,
    }: {
      customer_id?: string | number;
      subscription_id: string;
      event_id: string;
      price_with_discount: number;
    }) => {
      successPayment?.(subscription_id, event_id, price_with_discount);
      const localData = {
        upsell: analyticsParams?.is_upsell || false,
        subscription_id,
        plan_id: plan,
        ...(customer_id != null && customer_id !== '' ? { customer_id } : {}),
      };
      dispatch(setRequestResult({ isSuccess: true, id: subscription_id }));
      dispatch(setEventData(localData));
      sendAnalyticsEvents(EVENTS.SUBSCRIBED, { ...eventsData, ...localData });
      dispatch(setShowLoader(false));
    };

    const purchaseRequest = await getSubscriptionID(
      nonce,
      plan,
      token,
      analyticsParams,
      eventsData,
      coupon,
    );

    try {
      const { errors, data } = purchaseRequest;
      if (errors) {
        if (errors?.messages === 'User action required') {
          const { payment_method_nonce, validation_amount } = data;
          dispatch(setBraintreeNonce(payment_method_nonce));

          threeDSInstance.verifyCard(
            {
              nonce: payment_method_nonce,
              bin: binData,
              amount: validation_amount,
            },
            (verifyError: any, payload: any) => {
              if (verifyError) {
                console.error('verify error: ', verifyError);
                onError(verifyError, data);
                return;
              }
              // console.log('verify: ', payload);

              braintreePurchase({
                nonce: payload.nonce,
                binData,
                threeDSInstance,
                plan,
                token,
                analyticsParams,
                eventsData,
                successPayment,
              })(dispatch);
            },
          );
          return;
        }

        onError(errors?.messages || 'An error has occurred', data);
        return;
      }
      onSuccess(data);
    } catch (err) {
      onError(err);
    }
  };

export const sendBraintreeUpsellRequest =
  ({
    braintreeNonce,
    plan,
    token,
    analyticsParams,
    eventsData,
    onSuccessEvents,
    onErrorEvents,
  }: BraintreeUpsellProps) =>
  async (dispatch: any) => {
    function onError() {
      if (onErrorEvents) onErrorEvents(...arguments);
    }
    function onSuccess(data: {
      subscription_id: string;
      event_id: string;
      price_with_discount?: number;
      customer_id?: string | number;
    }) {
      const { subscription_id, event_id, price_with_discount, customer_id } = data || {};
      onSuccessEvents?.(
        subscription_id,
        event_id,
        price_with_discount,
        customer_id != null && customer_id !== '' ? { customer_id } : undefined,
      );
    }

    dispatch(setShowLoader(true));

    try {
      const { errors, data } = await getSubscriptionID(
        braintreeNonce,
        plan,
        token,
        analyticsParams,
        eventsData,
        '',
      );
      if (errors) {
        console.error(errors);
        onError();
        return;
      } else {
        onSuccess(data);
      }
    } catch (err) {
      console.error(err);
      onError();
    } finally {
      dispatch(setShowLoader(false));
    }
  };

export const selectCardholderName = (state: any) => state.checkout.cardholderName;
export const selectSuccess = (state: any) => state.checkout.isSuccess;
export const selectID = (state: any) => state.checkout.id;
export const selectPaymentError = (state: any) => state.checkout.paymentError;
export const selectIfCardFormShown = (state: any) => state.checkout.isCardFormShown;
export const selectStripeNonce = (state: any) => state.checkout.stripeNonce;
export const selectBraintreeNonce = (state: any) => state.checkout.braintreeNonce;
export const selectShowSuccessPopup = (state: any) => state.checkout.showSuccessPopup;
export const selectOpenPopoverId = (state: any) => state.checkout.openPopoverId;
export const selectShowUpsellGuideSuccessPopup = (state: any) =>
  Boolean(state.checkout.showUpsellGuideSuccessPopup);

export default checkoutSlice.reducer;

export { pixPurchase, completePixReturn } from '../../paymentMethods/pix/pixThunks';
export type {
  PixPurchaseProps,
  CompletePixReturnPending,
} from '../../paymentMethods/pix/pixThunks';
